import React, { FC, useCallback, useMemo } from 'react';

import { useFormContext, useWatch } from 'react-hook-form';

import { FontAwesomeIcon } from '@appcharge/shared-ui';
import { ESaleDiscountDisplayType } from 'constants/enums';
import { SALE_DISCOUNT_TYPE_OPTIONS } from 'views/Offers/Offer.consts';

import Input from 'components/Input/Input';
import InputSelectDropdown from 'components/InputSelectDropdown/InputSelectDropdown';
import RichText from 'components/RichText/RichText';
import {
  FormControl,
  FormField,
  FormItem,
  FormMessage
} from 'components/ui';

import { ISaleDiscountSectionProps } from './sections.types';

const DOLLAR_ICON = <FontAwesomeIcon icon="fa-regular fa-dollar-sign" />;
const PERCENT_ICON = <FontAwesomeIcon icon="fa-regular fa-percent" />;

const parseNumericInput = (
  raw: string,
  fallback: number | undefined
): number | undefined => {
  if (raw === '') return fallback;
  return parseFloat(raw);
};

const findTypeOption = (
  value: string | undefined
): { value: string; label: string } => {
  const resolved = value || ESaleDiscountDisplayType.PERCENTAGE;
  return (
    SALE_DISCOUNT_TYPE_OPTIONS.find((o) => o.value === resolved) ||
    SALE_DISCOUNT_TYPE_OPTIONS[0]
  );
};

export const SaleDiscountSection: FC<ISaleDiscountSectionProps> = ({
  showPriceDiscount,
  showCrossedPrice = false,
  saleLabel = 'Min Product Sale',
  saleTypeDataTestId,
  priceDiscountTypeDataTestId,
  saleInputDataTestId,
  discountInputDataTestId,
  saleDescriptionDataTestId,
  priceDiscountDescriptionDataTestId
}): React.ReactElement => {
  const { control } = useFormContext();

  const saleDisplayType = useWatch({
    control,
    name: 'salePercentageDisplayType',
    defaultValue: ESaleDiscountDisplayType.PERCENTAGE
  });

  const priceDiscountDisplayType = useWatch({
    control,
    name: 'priceDiscountDisplayType',
    defaultValue: ESaleDiscountDisplayType.PERCENTAGE
  });

  const saleLeftIcon = useMemo((): React.ReactElement | undefined => {
    if (saleDisplayType === ESaleDiscountDisplayType.PERCENTAGE) {
      return PERCENT_ICON;
    }
    return undefined;
  }, [saleDisplayType]);

  const discountLeftIcon = useMemo((): React.ReactElement | undefined => {
    if (priceDiscountDisplayType === ESaleDiscountDisplayType.PERCENTAGE) {
      return PERCENT_ICON;
    }
    return undefined;
  }, [priceDiscountDisplayType]);

  const saleTypeDefault = useMemo(
    () => findTypeOption(saleDisplayType),
    [saleDisplayType]
  );

  const discountTypeDefault = useMemo(
    () => findTypeOption(priceDiscountDisplayType),
    [priceDiscountDisplayType]
  );

  const handleSaleChange = useCallback(
    (onChange: (val: number) => void) =>
      (e: React.ChangeEvent<HTMLInputElement>): void => {
        onChange(parseNumericInput(e.target.value, 0) ?? 0);
      },
    []
  );

  const handleCrossedPriceChange = useCallback(
    (onChange: (val: number | undefined) => void) =>
      (e: React.ChangeEvent<HTMLInputElement>): void => {
        onChange(parseNumericInput(e.target.value, undefined));
      },
    []
  );

  const handleDiscountChange = useCallback(
    (onChange: (val: number) => void) =>
      (e: React.ChangeEvent<HTMLInputElement>): void => {
        onChange(parseNumericInput(e.target.value, 0) ?? 0);
      },
    []
  );

  let discountGridClass = 'grid-cols-2';
  if (showCrossedPrice) {
    discountGridClass = 'grid-cols-3';
  }

  const renderSalePercentage = useCallback(
    ({ field: { value, onChange } }: { field: { value: number; onChange: (v: number) => void } }): React.ReactElement => {
      const messageNode = <FormMessage />;
      const messageWithTestId = saleDescriptionDataTestId
        ? <span data-testid={saleDescriptionDataTestId}>{messageNode}</span>
        : messageNode;
      return (
        <FormItem>
          <FormControl>
            <Input
              name="salePercentage"
              label={saleLabel}
              type="number"
              min={0}
              step={0.1}
              leftIcon={saleLeftIcon}
              value={value ?? ''}
              onChange={handleSaleChange(onChange)}
              dataTestId={saleInputDataTestId}
            />
          </FormControl>
          {messageWithTestId}
        </FormItem>
      );
    },
    [saleLabel, saleLeftIcon, handleSaleChange, saleInputDataTestId, saleDescriptionDataTestId]
  );

  const renderSaleType = useCallback(
    ({ field: { onChange } }: { field: { onChange: (v: string) => void } }): React.ReactElement => (
      <FormItem>
        <FormControl>
          <InputSelectDropdown
            label="Type"
            placeholder="Select Type"
            options={SALE_DISCOUNT_TYPE_OPTIONS}
            defaultSelected={saleTypeDefault}
            onOptionChange={onChange}
            dataTestId={saleTypeDataTestId}
          />
        </FormControl>
      </FormItem>
    ),
    [saleTypeDefault, saleTypeDataTestId]
  );

  const renderCrossedPrice = useCallback(
    ({ field: { value, onChange } }: { field: { value: number | undefined; onChange: (v: number | undefined) => void } }): React.ReactElement => (
      <FormItem>
        <FormControl>
          <Input
            name="priceBeforeDiscount"
            label="Crossed price"
            type="number"
            min={0}
            step={0.1}
            leftIcon={DOLLAR_ICON}
            value={value ?? ''}
            onChange={handleCrossedPriceChange(onChange)}
          />
        </FormControl>
        <FormMessage />
      </FormItem>
    ),
    [handleCrossedPriceChange]
  );

  const renderDiscount = useCallback(
    ({ field: { value, onChange } }: { field: { value: number; onChange: (v: number) => void } }): React.ReactElement => {
      const messageNode = <FormMessage />;
      const messageWithTestId = priceDiscountDescriptionDataTestId
        ? <span data-testid={priceDiscountDescriptionDataTestId}>{messageNode}</span>
        : messageNode;
      return (
        <FormItem>
          <FormControl>
            <Input
              name="discount"
              label="Price Discount"
              type="number"
              min={0}
              step={0.1}
              leftIcon={discountLeftIcon}
              value={value ?? ''}
              onChange={handleDiscountChange(onChange)}
              dataTestId={discountInputDataTestId}
            />
          </FormControl>
          {messageWithTestId}
        </FormItem>
      );
    },
    [
      discountLeftIcon,
      handleDiscountChange,
      discountInputDataTestId,
      priceDiscountDescriptionDataTestId
    ]
  );

  const renderDiscountType = useCallback(
    ({ field: { onChange } }: { field: { onChange: (v: string) => void } }): React.ReactElement => (
      <FormItem>
        <FormControl>
          <InputSelectDropdown
            label="Type"
            placeholder="Select Type"
            disabled
            options={SALE_DISCOUNT_TYPE_OPTIONS}
            defaultSelected={discountTypeDefault}
            onOptionChange={onChange}
            dataTestId={priceDiscountTypeDataTestId}
          />
        </FormControl>
      </FormItem>
    ),
    [discountTypeDefault, priceDiscountTypeDataTestId]
  );

  return (
    <React.Fragment>
      <RichText
        title="Sale & Discounts"
        description="Enhance your offer by providing greater value at a better price"
        className="mb-5"
      />
      <div className="flex flex-col gap-5 mb-2">
        <div className="grid grid-cols-2 gap-5">
          <FormField control={control} name="salePercentage" render={renderSalePercentage} />
          <FormField control={control} name="salePercentageDisplayType" render={renderSaleType} />
        </div>
        {showPriceDiscount && (
          <div className={`grid gap-5 ${discountGridClass}`}>
            {showCrossedPrice && (
              <FormField control={control} name="priceBeforeDiscount" render={renderCrossedPrice} />
            )}
            <FormField control={control} name="discount" render={renderDiscount} />
            <FormField control={control} name="priceDiscountDisplayType" render={renderDiscountType} />
          </div>
        )}
      </div>
    </React.Fragment>
  );
};
